import React, { FC, useState } from "react";
import { Button, Col, Form, Input, Row, Select, Tooltip, message, notification } from "antd";
import styled from "@emotion/styled";
import { LanguageData } from "./AddVideosMetaDataForm";
import { authAxios } from "../../utils/session_utils";
import { CopyOutlined } from "@ant-design/icons";
import { CustomBtn } from "../../series/StyledComponent";
import { copyString, getVideoDataByGracenoteId, updateDataWithGracenoteData } from "../../utils";

export const TitleContainer = styled.div`
	border: 1px solid #ccc;
	border-radius: 8px;
	padding: 18px 16px 18px;
	position: relative;
	margin-bottom: 16px;
	margin-top: 30px;
`;

export const TitleFormText = styled.p`
	position: absolute;
	left: 14px;
	top: -12px;
	background: #fff;
	padding: 0 4px;
`;
type Props = {
	languagesList: LanguageData[];
	ele: any;
	index: any;
};
const MetaForm: FC<Props> = ({ languagesList, index }: Props) => {
	const [loading, setLoading] = useState(false);
	const [captionLoading, setCaptionLoading] = useState(false);
	const form = Form.useFormInstance();

	const updateWithGracenoteId = async () => {
		try {
			const gracenoteId = form.getFieldValue(`${index}_gracenote_episodic_tms_id`);
			if (!gracenoteId) {
				return notification.error({
					message: "Gracenote Episode TMS Id not found",
					description: "Please fill in the Gracenote Episode TMS Id",
				});
			}
			const { data } = await getVideoDataByGracenoteId(gracenoteId);
			updateDataWithGracenoteData(form, data, index);
			notification.success({
				message: null,
				description: "Data filled successfully in respective fields.",
			});
		} catch (error: any) {
			// setIsFormLoading(false);
			if (error?.response?.status === 404)
				return notification.error({
					message: null,
					description: "Provided Gracenote Episode TMS Id does not exist",
				});
			console.error("error in updateWithGracenoteId", error);
		}
	};

	// Utility function to call backend for description generation from caption
	const generateDescriptionsWithCaption = async () => {
		const description100 = form.getFieldValue(`${index}_description_100`);
		const description250 = form.getFieldValue(`${index}_description_250`);
		const descriptionFull = form.getFieldValue(`${index}_description_full`);
		if(description100 && description250 && descriptionFull){
			message.info("Descriptions already generated");
			return;
		}
		setCaptionLoading(true);
		try {
			const { video_ref_id: ref_id, audience_language } = form.getFieldsValue();
			const config = {
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/snowflake/description-from-caption`,
				headers: { "Content-Type": "application/json" },
				data: JSON.stringify({ ref_id, audience_language }),
			};
			const {data} = await authAxios(config);
			if (data.error) {
				message.error(data.error);
				return;
			}
			const {midDescription, shortDescription, fullDescription} = data;
			const updates: Record<string, string> = {};
			if (!description100 && shortDescription) {
				updates[`${index}_description_100`] = shortDescription;
			}
			if (!description250 && midDescription) {
				updates[`${index}_description_250`] = midDescription;
			}
			if (!descriptionFull && fullDescription) {
				updates[`${index}_description_full`] = fullDescription;
			}
			if (Object.keys(updates).length) {
				form.setFieldsValue(updates);
			}
			message.success("Descriptions updated successfully");
		} catch (error: any) {
			console.error("Error generating descriptions with caption:", error);
			message.error(error?.response?.data?.error || "Failed to generate descriptions from caption.");
		}
		setCaptionLoading(false);
	};

	const handleAutoShorten = async () => {
		const fullDesc = form.getFieldValue(`${index}_description_full`);
		const shortDesc = form.getFieldValue(`${index}_description_100`);
		const midDesc = form.getFieldValue(`${index}_description_250`);

		if (!fullDesc) return;

		const updates: Record<string, string> = {};
		let isShortDescRequired = false;
		let isMidDescRequired = false;

		if (!midDesc) {
			if(fullDesc.length <= 250){
				updates[`${index}_description_250`] = fullDesc;
			}else{
				isMidDescRequired = true;
			}
		}
		if (!shortDesc) {
			if(fullDesc.length <= 100){
				updates[`${index}_description_100`] = fullDesc;
			}else{
				isShortDescRequired = true;
			}
		}

		if(Object.keys(updates).length){
			form.setFieldsValue(updates);
		}
		if(isShortDescRequired || isMidDescRequired){
			try {
				const config = {
					method: "post",
					url: `${process.env.REACT_APP_BACK_END_API}/snowflake/short-description`,
					headers: { "Content-Type": "application/json" },
					data: JSON.stringify({ fullDesc }),
				};
				setLoading(true);
				const response = await authAxios(config);
				const {midDescription, shortDescription} = response.data;
				setLoading(false);
				const updateData: Record<string, string> = {};
				if(isMidDescRequired){
					updateData[`${index}_description_250`] = midDescription;
				}
				if(isShortDescRequired){
					updateData[`${index}_description_100`] = shortDescription;
				}

				if(Object.keys(updateData).length){
					form.setFieldsValue(updateData);
				}
			} catch (error) {
				setLoading(false)
				console.error("Error auto-generating descriptions:", error);
			}
		}
	};
	return (
		<TitleContainer key={index}>
			<TitleFormText>Metadata</TitleFormText>
			<Col span={12}>
				<Form.Item
					key={`${index}_metaLanguage`}
					name={`${index}_metaLanguage`}
					rules={[{ required: true, message: "Required Field Missing" }]}
				>
					<Select placeholder="Language">
						{languagesList?.map((language: LanguageData) => (
							<Select.Option value={language.NAME} key={language.ID}>
								{language.NAME}
							</Select.Option>
						))}
					</Select>
				</Form.Item>
			</Col>
			<Form.Item
				label={"Title"}
				key={`${index}_title`}
				name={`${index}_title`}
				rules={[{ required: true, message: "Required Field Missing" }]}
			>
				<Input placeholder="Title" showCount maxLength={150} />
			</Form.Item>
			<Form.Item label={"Clean Title"} key={`${index}_clean_title`} name={`${index}_clean_title`}>
				<Input placeholder="Clean Title" />
			</Form.Item>
			<Form.Item
				label={"Keywords"}
				key={`${index}_keywords`}
				name={`${index}_keywords`}
				// rules={[{ required: true, message: "Required Field Missing" }]}
			>
				<Input placeholder="Keywords" />
			</Form.Item>
			<Form.Item
				label={"Description 100"}
				key={`${index}_description_100`}
				name={`${index}_description_100`}
			>
				<Input.TextArea showCount maxLength={100} />
			</Form.Item>
			<Form.Item
				label={"Description 250"}
				key={`${index}_description_250`}
				name={`${index}_description_250`}
				// rules={[{ required: true, message: "Required Field" }]}
			>
				<Input.TextArea showCount maxLength={250} />
			</Form.Item>
			<Form.Item
				label={"Full Description"}
				key={`${index}_description_full`}
				name={`${index}_description_full`}
			>
				<Input.TextArea showCount maxLength={500} />
			</Form.Item>
			<Form.Item
				label="Gracenote Episodic TMS Id"
				name={`${index}_gracenote_episodic_tms_id`}
			>
				<Input 
					placeholder="Enter Gracenote Episodic TMS ID" 
					suffix={
						<CustomBtn
							style={{ cursor: "copy" }}
							title="Copy Gracenote Episodic TMS Id"
							icon={<CopyOutlined />}
							type={"link"}
							onClick={e => {
								e.stopPropagation();
								copyString(form.getFieldValue(`${index}_gracenote_episodic_tms_id`));
							}}
						/>
					}
				/>
			</Form.Item>
			<Row justify="end">
				<Tooltip title="Fills in empty data in both this video, and the parent episode, from Gracenote using the Gracenote Episode TMS Id to find the episode.">
					<Button onClick={updateWithGracenoteId} type="primary">
						Gracenote Update
					</Button>
				</Tooltip> &nbsp;
				<Button loading={captionLoading} type="primary" onClick={generateDescriptionsWithCaption}>Description from Captions</Button> &nbsp;
				<Button loading={loading} type="primary" onClick={handleAutoShorten}>Auto Shorten</Button>
			</Row>
		</TitleContainer>
	);
};

export default MetaForm;
